import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { WhatsappLogo, Check, Checks, WarningCircle } from "@phosphor-icons/react";

const LABEL = { sent: "Sent", delivered: "Delivered", read: "Read", failed: "Failed", mocked: "Sent (test mode)" };

function _ago(iso) {
    if (!iso) return "";
    const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
    return m < 1 ? "just now" : m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`;
}

function StatusLine({ msg }) {
    if (!msg) return null;
    const s = msg.status;
    const Icon = s === "read" || s === "delivered" ? Checks : s === "failed" ? WarningCircle : Check;
    const tone = s === "read" ? "text-sky-600" : s === "failed" ? "text-red-600" : "text-neutral-600";
    const at = msg[`${s}_at`] || msg.created_at;
    return (
        <p className={`text-[11px] font-mono flex items-center gap-1 ${tone}`} data-testid="crm-wa-status">
            <Icon size={13} weight="bold" />
            <span data-testid="crm-wa-status-label">{LABEL[s] || s}</span>
            <span className="text-neutral-400">· {_ago(at)}</span>
            {s === "failed" && msg.error && <span className="truncate" title={msg.error}>· {msg.error}</span>}
        </p>
    );
}

export const WhatsAppLoginLinkButton = ({ lead, disabled, onSent }) => {
    const [info, setInfo] = useState(null);
    const [busy, setBusy] = useState(false);
    const [now, setNow] = useState(Date.now());

    const load = useCallback(() => {
        api.get(`/crm/leads/${lead.id}/whatsapp-status`).then((r) => setInfo(r.data)).catch(() => {});
    }, [lead.id]);

    useEffect(() => { load(); }, [load]);
    const live = ["sent", "delivered"].includes(info?.last_message?.status);
    useEffect(() => {
        const t = setInterval(() => { setNow(Date.now()); if (live) load(); }, 15000);
        return () => clearInterval(t);
    }, [live, load]);

    const sentAt = info?.wa_login_link_sent_at ? new Date(info.wa_login_link_sent_at).getTime() : 0;
    const waitMin = Math.ceil((sentAt + (info?.cooldown_minutes || 10) * 60000 - now) / 60000);
    const cooling = sentAt && waitMin > 0;

    async function send() {
        setBusy(true);
        try {
            const r = await api.post(`/crm/leads/${lead.id}/whatsapp-login-link`);
            toast.success(r.data.link_kind === "magic" ? "Login link sent on WhatsApp" : "Sign-up link sent on WhatsApp");
            load();
            onSent?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send WhatsApp link");
            load();
        } finally { setBusy(false); }
    }

    return (
        <div className="space-y-1">
            <button
                onClick={send}
                disabled={disabled || busy || cooling || !lead.phone}
                data-testid="crm-drawer-wa-login-link"
                title={!lead.phone ? "Needs a phone number" : !lead.email ? "No email — sends a sign-up link with phone pre-filled" : ""}
                className="w-full bg-emerald-600 text-white hover:bg-emerald-700 px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-40 transition-colors"
            >
                <WhatsappLogo size={15} weight="bold" />
                {busy ? "Sending…" : cooling ? `Resend in ${waitMin}m` : lead.email ? "Send login link on WhatsApp" : "Send sign-up link on WhatsApp"}
            </button>
            <StatusLine msg={info?.last_message} />
        </div>
    );
};
